import React, { useContext, useMemo, useState } from 'react';
import { LorawanActionTypes } from '@utils/deviceActions';
import rolesConstants from '@utils/rolesConstants';
import { useTranslation } from 'react-i18next';
import { Select } from 'antd';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';

interface ImportPayloadProps {
  devEui: string;
  newCustomerVirtualAccountId: string;
}

interface ImportProps {
  devices: ImportPayloadProps[];
}

const useImport = ({ devices }: ImportProps) => {
  const { t } = useTranslation('lorawan');
  const [contractId, setContractId] = useState();
  const { hasPermission } = useContext(SessionContext);

  const hasNewLora = hasPermission(rolesConstants.LORAWAN_NEW);

  const { data: contractsData } = useSwr('/service-proxy/contracts', {
    params: {
      product: 'MOT',
    },
  });

  const contracts = useMemo(
    () =>
      Array.isArray(contractsData)
        ? contractsData?.map(({ id, assetNumber, planName, nickname }) => ({
            value: hasNewLora ? id : assetNumber,
            label: `${assetNumber}${
              nickname ? ` - ${nickname}` : ''
            } - ${planName}`,
          }))
        : [],
    [contractsData, hasNewLora],
  );

  const steps = useMemo(
    () => [
      {
        title: t('contract'),
        name: 'contract',
        showWhenUpload: true,
        content: (
          <Select
            loading={!contracts}
            optionFilterProp="label"
            options={contracts}
            value={contractId}
            allowClear
            showSearch
            onChange={value => setContractId(value)}
          />
        ),
      },
    ],
    [contractId, contracts, t],
  );

  const payloadToSend = useMemo(
    () => ({
      action: LorawanActionTypes.IMPORT,
      contractId,
      devices,
    }),
    [contractId, devices],
  );

  return {
    steps,
    uploadPosition: 1,
    title: t(`device-actions:labels.lorawan.${rolesConstants.LORAWAN_IMPORT}`),
    payloadToSend,
  };
};

export default useImport;
